import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import Select from "react-select";
import { ActionMeta, MultiValue } from "react-select";
import toast, { Toaster } from "react-hot-toast";
import OverlayLoader from "../Components/OverlayLoader";
import {
  fetchLocationData,
  fetchProjectEditData,
  updateProject,
  fetchUserData,
  assignTargetToUsers,
  fetchAllAssigedObjects,
} from "../Services/APIService";

interface EditProjectDemoProps {
  isOpen: boolean;
  onClose: () => void;
  projectData?: any;
  onSubmit?: (data: any) => void;
}

interface ValidationErrors {
  projectName?: string;
  description?: string;
  locationID?: string;
  contactMails?: string;
  assignmentType?: string;
  assignmentTypeId?: string;
  listOfUsers?: string;
}

interface Location {
  locationID: number;
  locationName: string;
  isActive?: boolean;
}

interface Project {
  projectID: number;
  projectName: string;
  description: string;
  locationID: number;
  contactMails: string;
  brochure: string;
  priceQuote: string;
  tenantId: number;
  isActive: boolean;
}

interface AssignmentData {
  tenantId: number;
  assignmentTypeId: number;
  assignmentObjectId: number;
  isAssigned: boolean;
  listOfUsers: {
    userId: number;
  }[];
  createdBy: number;
}

interface AssignmentUser {
  userId: number;
  isActive: boolean;
}

interface AssignmentDatas {
  tenantId: number;
  assignmentTypeId: number;
  assignmentObjectId: number;
  isAssigned: boolean;
  listOfUsers: AssignmentUser[];
  createdBy: number;
}

interface User {
  userId: number;
  userName: string;
  email: string;
  isActive?: boolean;
}

interface SelectOption {
  value: number;
  label: string;
  userData: User;
}

const EditProjectDemo: React.FC<EditProjectDemoProps> = ({
  isOpen,
  onClose,
  projectData: initialProjectData,
  onSubmit,
}) => {
  const navigate = useNavigate();

  const [activeTab, setActiveTab] = useState("home");
  const [isLoading, setIsLoading] = useState(true);
  const [errors, setErrors] = useState<ValidationErrors>({});
  const [locations, setLocations] = useState<Location[]>([]);

  // State for project data
  const [projectData, setProjectData] = useState<Project | null>(null);

  // State for contact emails input
  const [emailInput, setEmailInput] = useState("");
  const [emails, setEmails] = useState<string[]>([]);

  // State for file uploads
  const [brochureFile, setBrochureFile] = useState<File | null>(null);
  const [priceQuoteFile, setPriceQuoteFile] = useState<File | null>(null);

  // State to track if files have been changed
  const [brochureChanged, setBrochureChanged] = useState(false);
  const [priceQuoteChanged, setPriceQuoteChanged] = useState(false);

  const [assignmentData, setAssignmentData] = useState<AssignmentDatas>({
    tenantId: 0,
    assignmentTypeId: 2,
    assignmentObjectId: 0,
    isAssigned: true,
    listOfUsers: [],
    createdBy: 0,
  });

  // User assignment states
  const [users, setUsers] = useState<User[]>([]);
  const [userOptions, setUserOptions] = useState<SelectOption[]>([]);
  const [selectedUsers, setSelectedUsers] = useState<SelectOption[]>([]);
  const [isLoadingUsers, setIsLoadingUsers] = useState(true);
  const [isSubmittingAssignment, setIsSubmittingAssignment] = useState(false);
  const [isLoadingAssignedUsers, setIsLoadingAssignedUsers] = useState(false);
  const [initialAssignedUsers, setInitialAssignedUsers] = useState<number[]>(
    []
  );

  useEffect(() => {
    if (isOpen) {
      fetchInitialData();
    }
  }, [isOpen]);

  const fetchInitialData = async () => {
    setIsLoading(true);
    try {
      const [locationData, usersData] = await Promise.all([
        fetchLocationData(),
        fetchUserData(),
      ]);
      console.log("Sample location object:", locationData[0]);
      console.log("IsActive value:", locationData[0]?.isActive);
      console.log("IsActive type:", typeof locationData[0]?.isActive);

      const activeLocations = locationData.filter(
        (location: Location) => location.isActive === true
      );
      console.log("Fetched locations:", locationData);
      console.log("Fetched locations:", activeLocations);
      const activeUsers = usersData.filter(
        (user: User) => user.isActive === true
      );
      setLocations(activeLocations);
      setUsers(activeUsers);

      const options = usersData.map((user) => ({
        value: user.userId,
        label: `${user.userName} (${user.email})`,
        userData: user,
      }));
      setUserOptions(options);

      // Load project data if provided
      if (initialProjectData) {
        setActiveTab(initialProjectData.isAssignTabShown ? "contact" : "home");
        console.log("Initial project data:", initialProjectData);
        await loadProjectData(initialProjectData, options);
      }
    } catch (error) {
      console.error("Error fetching data:", error);
      toast.error("Failed to load required data");
    } finally {
      setIsLoading(false);
      setIsLoadingUsers(false);
    }
  };

  const loadProjectData = async (projectInfo: any, options: SelectOption[]) => {
    try {
      setProjectData(projectInfo);

      setAssignmentData((prev) => ({
        ...prev,
        assignmentObjectId: projectInfo.projectID,
        tenantId: parseInt(localStorage.getItem("tenantId") || "0"),
        createdBy: parseInt(localStorage.getItem("UserId") || "0"),
      }));

      if (projectInfo.contactMails) {
        setEmails(
          projectInfo.contactMails
            .split(",")
            .map((email: string) => email.trim())
        );
      }

      // Load assigned users
      setIsLoadingAssignedUsers(true);
      try {
        const fetchedAssignedUsers = await fetchAllAssigedObjects(
          projectInfo.projectID,
          2
        );
        const assignedUsersData: AssignmentUser[] = fetchedAssignedUsers.map(
          (user) => ({
            userId: user.userId,
            isActive: true,
          })
        );

        const preSelected = options.filter((option) =>
          assignedUsersData.some(
            (assignedUser) => assignedUser.userId === option.value
          )
        );
        setSelectedUsers(preSelected);

        const initialUserIds = assignedUsersData.map((user) => user.userId);
        setInitialAssignedUsers(initialUserIds);

        setAssignmentData((prev) => ({
          ...prev,
          listOfUsers: assignedUsersData,
        }));
      } catch (err) {
        console.error("Failed to fetch assigned users:", err);
        toast.error("Failed to fetch assigned users");
      } finally {
        setIsLoadingAssignedUsers(false);
      }
    } catch (error) {
      console.error("Error loading project data:", error);
      toast.error("Failed to load project data");
    }
  };

  const handleTabClick = (tabId: string) => {
    setActiveTab(tabId);
  };

  const handleChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
    >
  ) => {
    const { name, value, type } = e.target;

    // Handle checkbox inputs
    if (type === "checkbox") {
      const checked = (e.target as HTMLInputElement).checked;
      setProjectData((prev) => (prev ? { ...prev, [name]: checked } : null));
      return;
    }

    // Prevent leading spaces for all text inputs (including textarea)
    if (value.startsWith(" ")) {
      return;
    }

    // Update project data
    setProjectData((prev) => (prev ? { ...prev, [name]: value } : null));

    // Clear validation errors for this field
    if (errors[name as keyof ValidationErrors]) {
      setErrors({
        ...errors,
        [name]: undefined,
      });
    }
  };

  const handleEmailInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEmailInput(e.target.value);
  };

  const handleEmailKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter" || e.key === "," || e.key === " ") {
      e.preventDefault();
      addEmail();
    }
  };

  const addEmail = () => {
    const trimmedEmail = emailInput.trim();
    if (trimmedEmail && validateEmail(trimmedEmail)) {
      if (!emails.includes(trimmedEmail)) {
        setEmails([...emails, trimmedEmail]);
        if (projectData) {
          setProjectData({
            ...projectData,
            contactMails: [...emails, trimmedEmail].join(","),
          });
        }
        setEmailInput("");
        if (errors.contactMails) {
          setErrors({
            ...errors,
            contactMails: undefined,
          });
        }
      }
    } else if (trimmedEmail) {
      setErrors({
        ...errors,
        contactMails: "Invalid email format",
      });
    }
  };

  const removeEmail = (emailToRemove: string) => {
    const updatedEmails = emails.filter((email) => email !== emailToRemove);
    setEmails(updatedEmails);
    if (projectData) {
      setProjectData({
        ...projectData,
        contactMails: updatedEmails.join(","),
      });
    }
  };

  const handleFileChange = (
    e: React.ChangeEvent<HTMLInputElement>,
    fileType: "brochure" | "priceQuote"
  ) => {
    const file = e.target.files && e.target.files[0];
    if (file) {
      // Check if file is PDF
      const isPDF =
        file.type === "application/pdf" ||
        file.name.toLowerCase().endsWith(".pdf");

      if (!isPDF) {
        toast.success("Please select a PDF file only.");
        // Clear the input
        e.target.value = "";
        return;
      }

      if (fileType === "brochure") {
        setBrochureFile(file);
        setBrochureChanged(true);
      } else {
        setPriceQuoteFile(file);
        setPriceQuoteChanged(true);
      }
    }
  };

  const fileToBase64 = (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => {
        const base64String = reader.result as string;
        const base64Content = base64String.split(",")[1];
        resolve(base64Content);
      };
      reader.onerror = (error) => reject(error);
    });
  };

  const validateEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const validateForm = (): boolean => {
    const newErrors: ValidationErrors = {};
    let isValid = true;

    if (!projectData?.projectName.trim()) {
      newErrors.projectName = "Project name is required";
      isValid = false;
    }

    if (!projectData?.locationID) {
      newErrors.locationID = "Location is required";
      isValid = false;
    }

    if (!projectData?.description.trim()) {
      newErrors.description = "Description is required";
      isValid = false;
    }

    setErrors(newErrors);
    return isValid;
  };

  const validateAssignmentData = (): boolean => {
    const newErrors: ValidationErrors = {};
    let isValid = true;

    if (!selectedUsers || selectedUsers.length === 0) {
      newErrors.listOfUsers = "Please select at least one user";
      isValid = false;
    }

    setErrors(newErrors);
    return isValid;
  };

  const handleMultiSelectChange = (
    newValue: MultiValue<SelectOption>,
    actionMeta: ActionMeta<SelectOption>
  ) => {
    if (!newValue || newValue.length === 0) {
      setSelectedUsers([]);
      const updatedList = assignmentData.listOfUsers.map((user) => ({
        userId: user.userId,
        isActive: false,
      }));
      setAssignmentData({
        ...assignmentData,
        listOfUsers: updatedList,
      });
      return;
    }

    const selectedOptions = Array.isArray(newValue) ? newValue : [newValue];
    setSelectedUsers(selectedOptions);

    const selectedUserIds = selectedOptions.map((option) => option.value);

    const updatedListOfUsers: AssignmentUser[] = assignmentData.listOfUsers.map(
      (user) => ({
        userId: user.userId,
        isActive: selectedUserIds.includes(user.userId),
      })
    );

    setAssignmentData({
      ...assignmentData,
      listOfUsers: updatedListOfUsers,
    });

    if (errors.listOfUsers) {
      setErrors({
        ...errors,
        listOfUsers: undefined,
      });
    }
  };

  const handleSubmit = async () => {
    if (activeTab === "contact") {
      await handleAssignmentSubmit();
    } else {
      await handleProjectSubmit();
    }
  };

  const handleProjectSubmit = async () => {
    if (!validateForm() || !projectData) {
      //toast.error("Please fix the errors in the form");
      return;
    }

    try {
      let brochureData = projectData.brochure;
      let priceQuoteData = projectData.priceQuote;

      if (brochureChanged && brochureFile) {
        brochureData = await fileToBase64(brochureFile);
      }

      if (priceQuoteChanged && priceQuoteFile) {
        priceQuoteData = await fileToBase64(priceQuoteFile);
      }

      const userId = localStorage.getItem("UserId");
      const tenantId = localStorage.getItem("tenantId");

      const updateData = {
        projectID: projectData.projectID,
        projectName: projectData.projectName,
        description: projectData.description,
        locationID: projectData.locationID,
        isActive: projectData.isActive,
        contactMails: emails.join(","),
        brochure: brochureData,
        priceQuote: priceQuoteData,
        tenantId: tenantId ? parseInt(tenantId) : 0,
        modifieddBy: userId ? parseInt(userId) : 0,
      };

      const result = await updateProject(updateData);

      if (result.isUpdated) {
        toast.success("Project updated successfully");
        if (onSubmit) {
          onSubmit(updateData);
        }
        setTimeout(() => {
          handleClose();
        }, 1000);
      } else {
        toast.error(result.message || "Failed to update project");
      }
    } catch (error) {
      console.error("Error updating project:", error);
      toast.error("An error occurred while updating the project");
    }
  };

  const handleAssignmentSubmit = async () => {
    if (!validateAssignmentData()) {
      toast.error("Please select at least one user.");
      return;
    }

    try {
      setIsSubmittingAssignment(true);

      const currentSelectedUserIds = selectedUsers.map((user) => user.value);
      const usersToUnassign = initialAssignedUsers.filter(
        (userId) => !currentSelectedUserIds.includes(userId)
      );
      const usersToAssign = currentSelectedUserIds;

      // Perform unassign operation if there are users to unassign
      if (usersToUnassign.length > 0) {
        const unassignData: AssignmentData = {
          tenantId: assignmentData.tenantId,
          assignmentTypeId: assignmentData.assignmentTypeId,
          assignmentObjectId: assignmentData.assignmentObjectId,
          isAssigned: false,
          listOfUsers: usersToUnassign.map((userId) => ({ userId })),
          createdBy: assignmentData.createdBy,
        };

        const unassignResult = await assignTargetToUsers(unassignData);
        if (!unassignResult.isUpdated) {
          throw new Error(
            unassignResult.message || "Failed to unassign users."
          );
        }
      }

      // Perform assign operation if there are users to assign
      if (usersToAssign.length > 0) {
        const assignData: AssignmentData = {
          tenantId: assignmentData.tenantId,
          assignmentTypeId: assignmentData.assignmentTypeId,
          assignmentObjectId: assignmentData.assignmentObjectId,
          isAssigned: true,
          listOfUsers: usersToAssign.map((userId) => ({ userId })),
          createdBy: assignmentData.createdBy,
        };

        const assignResult = await assignTargetToUsers(assignData);
        if (!assignResult.isUpdated) {
          throw new Error(assignResult.message || "Failed to assign users.");
        }

        toast.success(assignResult.message || "Users updated successfully");
      }

      // Update initial assigned users
      setInitialAssignedUsers(currentSelectedUserIds);

      if (onSubmit) {
        onSubmit({ assignedUsers: currentSelectedUserIds });
      }

      // Switch to the "Project Details" tab
      setActiveTab("home");
    } catch (error) {
      console.error("Error updating user assignments:", error);
      toast.error(
        error instanceof Error
          ? error.message
          : "An error occurred while updating user assignments."
      );
    } finally {
      setIsSubmittingAssignment(false);
    }
  };

  const handleClose = () => {
    // Reset form when closing
    setProjectData(null);
    setEmails([]);
    setEmailInput("");
    setBrochureFile(null);
    setPriceQuoteFile(null);
    setBrochureChanged(false);
    setPriceQuoteChanged(false);
    setSelectedUsers([]);
    setErrors({});
    setActiveTab("home");
    onClose();
  };

  const requiredField = <span className="text-danger ms-1">*</span>;

  const tabs = [
    { id: "home", name: "Project Details" },
    { id: "files", name: "Files & Contact Info" },
    { id: "contact", name: "Assigners Information" },
  ];

  if (!isOpen) return null;

  return (
    <>
      <div>
        <Toaster position="top-center" reverseOrder={false} />
      </div>

      <div
        className={`modal-backdrop-custom ${isOpen ? "open" : "closed"}`}
        onClick={handleClose}
      ></div>

      <div className={`modal-custom ${isOpen ? "open" : "closed"}`}>
        <div className="modal-content ProjectEditFormModal">
          <div className="modal-header-custom">
            <h5 className="modal-title-custom">Edit Project</h5>
            <button
              type="button"
              className="btn-close-custom"
              onClick={handleClose}
            >
              <span className="icon-close"></span>
            </button>
          </div>

          <ul
            className="nav nav-tabs pop-up-tabHeader"
            id="myTab"
            role="tablist"
            style={{ borderBottom: "1px solid rgb(206 212 218)" }}
          >
            {tabs.map((tab) => (
              <li key={tab.id} className="nav-item" role="presentation">
                <button
                  className={`nav-link card-title tab-btn pop-up-tabHeaderbtn ${
                    activeTab === tab.id ? "active" : ""
                  }`}
                  id={`tab-${tab.id}`}
                  onClick={() => handleTabClick(tab.id)}
                  type="button"
                  role="tab"
                  aria-controls={`tab-${tab.id}-pane`}
                  aria-selected={activeTab === tab.id}
                >
                  {tab.name}
                </button>
              </li>
            ))}
          </ul>

          <div className="modal-body-custom">
            <div className="card pop-up-card">
              <div className="tab-content card-body p-0" id="myTabContent">
                {/* Project Details Tab */}
                <div
                  className={`tab-pane fade ${
                    activeTab === "home" ? "show active" : ""
                  }`}
                  id="home-tab-pane"
                  role="tabpanel"
                  aria-labelledby="home-tab"
                  tabIndex={0}
                >
                  <div className="modal-body-content">
                    <form>
                      <div className="row">
                        <div className="col-12 col-lg-6 mb-3 input-group-custom">
                          <label className="form-label">
                            Project Name{requiredField}
                          </label>
                          <input
                            name="projectName"
                            placeholder="Project Name"
                            value={projectData?.projectName || ""}
                            onChange={handleChange}
                            required
                            className={`form-control-custom form-control-custom1 ${
                              errors.projectName ? "is-invalid" : ""
                            }`}
                          />
                          {errors.projectName && (
                            <div className="invalid-feedback-custom">
                              {errors.projectName}
                            </div>
                          )}
                        </div>

                        <div className="col-12 col-lg-6 mb-3 input-group-custom">
                          <label className="form-label">
                            Location{requiredField}
                          </label>
                          <select
                            name="locationID"
                            value={projectData?.locationID || ""}
                            onChange={handleChange}
                            required
                            className={`form-control-custom form-control-custom1 ${
                              errors.locationID ? "is-invalid" : ""
                            }`}
                          >
                            <option value="">Select Location</option>
                            {locations.map((location) => (
                              <option
                                key={location.locationID}
                                value={location.locationID}
                              >
                                {location.locationName}
                              </option>
                            ))}
                          </select>
                          {errors.locationID && (
                            <div className="invalid-feedback-custom">
                              {errors.locationID}
                            </div>
                          )}
                        </div>

                        <div className="col-12 mb-3 input-group-custom">
                          <label className="form-label">
                            Description{requiredField}
                          </label>
                          <textarea
                            name="description"
                            placeholder="Description"
                            value={projectData?.description || ""}
                            onChange={handleChange}
                            required
                            rows={4}
                            className={`form-control-custom-textarea ${
                              errors.description ? "is-invalid" : ""
                            }`}
                          />
                          {errors.description && (
                            <div className="invalid-feedback-custom">
                              {errors.description}
                            </div>
                          )}
                        </div>

                        <div className="col-12 col-lg-6 mb-3 input-group-custom">
                          <div className="form-check">
                            <input
                              type="checkbox"
                              name="isActive"
                              checked={projectData?.isActive || false}
                              onChange={handleChange}
                              className="form-check-input"
                            />
                            <label className="form-check-label">
                              Is Active
                            </label>
                          </div>
                        </div>
                      </div>
                    </form>
                  </div>
                </div>

                {/* Files & Contact Info Tab */}
                <div
                  className={`tab-pane fade ${
                    activeTab === "files" ? "show active" : ""
                  }`}
                  id="files-tab-pane"
                  role="tabpanel"
                  aria-labelledby="files-tab"
                  tabIndex={0}
                >
                  <div className="modal-body-content">
                    <form>
                      <div className="row">
                        <div className="col-12 col-lg-6 mb-3 input-group-custom">
                          <label className="form-label">Brochure</label>
                          <input
                            type="file"
                            className="form-control-custom form-control-custom1"
                            accept=".pdf,application/pdf"
                            onChange={(e) => handleFileChange(e, "brochure")}
                          />
                          {projectData?.brochure && !brochureChanged && (
                            <div className="form-text text-success">
                              Brochure file already uploaded
                            </div>
                          )}
                          {brochureFile && (
                            <div className="form-text">
                              Selected: {brochureFile.name}
                            </div>
                          )}
                          <div className="form-text text-danger">
                            Only PDF files are allowed
                          </div>
                        </div>

                        <div className="col-12 col-lg-6 mb-3 input-group-custom">
                          <label className="form-label">Price Quote</label>
                          <input
                            type="file"
                            className="form-control-custom form-control-custom1"
                            accept=".pdf,application/pdf"
                            onChange={(e) => handleFileChange(e, "priceQuote")}
                          />
                          {projectData?.priceQuote && !priceQuoteChanged && (
                            <div className="form-text text-success">
                              Price quote file already uploaded
                            </div>
                          )}
                          {priceQuoteFile && (
                            <div className="form-text">
                              Selected: {priceQuoteFile.name}
                            </div>
                          )}
                          <div className="form-text text-danger">
                            Only PDF files are allowed
                          </div>
                        </div>

                        <div className="col-12 mb-3 input-group-custom">
                          <label className="form-label">Contact Mails</label>
                          <div
                            className={`form-control-custom ${
                              errors.contactMails ? "is-invalid" : ""
                            }`}
                            style={{
                              height: "auto",
                              minHeight: "60px",
                              display: "flex",
                              flexWrap: "wrap",
                              gap: "5px",
                              alignItems: "flex-start",
                              alignContent: "flex-start",
                              padding: "8px",
                            }}
                          >
                            {emails.map((email, index) => (
                              <div
                                key={index}
                                className="badge bg-primary p-2 m-1"
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                  color: "rgb(255 255 255)",
                                  backgroundImage:
                                    "linear-gradient(90deg, rgba(131,131,232,1) 0%, rgba(0,0,128,1) 100%)",
                                  borderRadius: "10px",
                                }}
                              >
                                {email}
                                <span
                                  className="ms-2"
                                  style={{ cursor: "pointer" }}
                                  onClick={() => removeEmail(email)}
                                >
                                  ×
                                </span>
                              </div>
                            ))}
                            <input
                              type="email"
                              value={emailInput}
                              onChange={handleEmailInputChange}
                              onKeyDown={handleEmailKeyDown}
                              onBlur={addEmail}
                              placeholder="Type email and press Enter, space or comma"
                              style={{
                                flex: "1",
                                border: "none",
                                outline: "none",
                                minWidth: "150px",
                                background: "transparent",
                              }}
                            />
                          </div>
                          {errors.contactMails && (
                            <div className="invalid-feedback-custom">
                              {errors.contactMails}
                            </div>
                          )}
                          <div className="form-text">
                            Type email and press Enter, space or comma to add
                          </div>
                        </div>
                      </div>
                    </form>
                  </div>
                </div>

                {/* Assigners Information Tab */}
                <div
                  className={`tab-pane fade ${
                    activeTab === "contact" ? "show active" : ""
                  }`}
                  id="contact-tab-pane"
                  role="tabpanel"
                  aria-labelledby="contact-tab"
                  tabIndex={0}
                >
                  <div className="modal-body-content">
                    <form>
                      <div className="row">
                        <div className="col-12 mb-3 input-group-custom">
                          <label className="form-label">
                            Assign Users{requiredField}
                          </label>
                          <Select
                            options={userOptions}
                            value={selectedUsers}
                            onChange={handleMultiSelectChange}
                            isMulti
                            className={`basic-multi-select ${
                              errors.listOfUsers ? "is-invalid" : ""
                            }`}
                            classNamePrefix="select"
                            placeholder="Select users to assign"
                            isLoading={isLoadingUsers || isLoadingAssignedUsers}
                            noOptionsMessage={() => "No users available"}
                          />
                          {errors.listOfUsers && (
                            <div className="invalid-feedback-custom">
                              {errors.listOfUsers}
                            </div>
                          )}
                          <div className="form-text">
                            Select multiple users to assign to this project
                          </div>
                        </div>
                        <div className="col-12 mb-3 input-group-custom"></div>
                        <div className="col-12 mb-3 input-group-custom"></div>
                        <div className="col-12 mb-3 input-group-custom"></div>
                        <div className="col-12 mb-3 input-group-custom"></div>
                        <div className="col-12 mb-3 input-group-custom"></div>
                        <div className="col-12 mb-3 input-group-custom"></div>
                        <div className="col-12 mb-3 input-group-custom"></div>
                        <div className="col-12 mb-3 input-group-custom"></div>
                      </div>
                    </form>
                  </div>
                </div>
              </div>
            </div>

            <div className="modal-footer-custom">
              <div>
                <button
                  type="button"
                  onClick={handleClose}
                  className="btn-custom btn-cancel"
                  disabled={isSubmittingAssignment}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn-custom btn-primary-custom"
                  onClick={handleSubmit}
                  disabled={isLoading || isSubmittingAssignment}
                >
                  {isSubmittingAssignment
                    ? "Updating..."
                    : activeTab === "contact"
                    ? "Save Assigner"
                    : "Update Project"}
                </button>
              </div>
            </div>
          </div>
        </div>
        <OverlayLoader
          loading={isLoadingUsers || isLoadingAssignedUsers}
          message="Loading Data"
          submessage="This might take a moment..."
        />
      </div>
    </>
  );
};

export default EditProjectDemo;
